import React from 'react';
import classNames from 'classnames/bind';
import { CellProps, Column } from 'react-table';
import { useDebouncedValue } from '@tanstack/react-pacer';

import Pipeline from 'types/pipeline';
import { Category } from 'types/category';
import { PipelineTemplateSummary } from 'types/pipeline_template';

import { isIconType } from 'types/icon';
import { useCategories } from 'hooks/api/useCategories';
import { useCreatePipelineFromTemplate } from 'pipelines/hooks/useCreatePipelineFromTemplate';
import { usePipelineTemplates } from 'pipelines/hooks/usePipelineTemplates';

import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';

import {
  CategorySelect,
  HighlightedCategoryFilter,
} from 'components/CategorySelect';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { Loader } from 'components/Loader/Loader';
import { PaginatedTable } from 'components/Table/PaginatedTable';
import { QuerySearch } from 'components/QuerySearch/QuerySearch';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { Text } from 'components/Text/Text';

import styles from './PipelinePicker.module.scss';

const c = classNames.bind(styles);

const PAGE_SIZE = 20;

const columns: Column<PipelineTemplateSummary>[] = [
  {
    Cell({ row: { original } }: CellProps<PipelineTemplateSummary>) {
      return (
        <div className={c('template-img-wrap')}>
          {original.gallery_img_url ? (
            <img
              className={c('template-img')}
              src={original.gallery_img_url}
              alt=""
            />
          ) : (
            <div className={c('template-img')}>Preview unavailable</div>
          )}
        </div>
      );
    },
    accessor: 'gallery_img_url',
  },
  {
    Cell({ row: { original } }: CellProps<PipelineTemplateSummary>) {
      return (
        <div className={c('template-content')}>
          {original.categories.length > 0 && (
            <div className={c('template-categories')}>
              {original.categories.map(({ label, icon_name }) => (
                <Text
                  className={c('template-category')}
                  size="small"
                  key={label}
                  inline
                >
                  {isIconType(icon_name) && (
                    <Icon name={icon_name} size="small" />
                  )}
                  <span>{label}</span>
                </Text>
              ))}
            </div>
          )}
          <Heading level="4" asChild>
            <strong>{original.name}</strong>
          </Heading>
          <Text type="paragraph">{original.description}</Text>
        </div>
      );
    },
    accessor: 'name',
  },
];

export type PipelineTemplatesListProps = {
  onSelect: (pipeline: Pipeline) => void;
};

export function PipelineTemplatesList({
  onSelect,
}: PipelineTemplatesListProps) {
  const [page, setPage] = React.useState(1);
  const [hasAutoSelected, setHasAutoSelected] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState('');
  const [debouncedSearchInput] = useDebouncedValue(searchInput, { wait: 250 });

  const [highlightedCategories, setHighlightedCategories] = React.useState<
    Category[]
  >([]);
  const [moreCategories, setMoreCategories] = React.useState<Category[]>([]);

  const categories = [...highlightedCategories, ...moreCategories];

  // Get current plan information
  const { data: currentPlan, isSimplifiedUxPlan } = useCurrentPlan();

  // Fetch all categories to find matching plan category
  const { data: allCategories } = useCategories({
    params: {
      limit: 100, // Get all categories
    },
  });

  // Auto-select category matching plan name on component mount
  React.useEffect(() => {
    // Only auto-select once and only if user hasn't manually selected anything
    if (
      !hasAutoSelected &&
      currentPlan?.name &&
      allCategories?.data &&
      highlightedCategories.length === 0 &&
      moreCategories.length === 0
    ) {
      // Find category that matches the plan name (case-insensitive)
      const matchingCategory = allCategories.data.find(
        (category) =>
          category.label.toLowerCase() === currentPlan.name.toLowerCase()
      );

      if (matchingCategory) {
        setHasAutoSelected(true);
        // Check if it's a highlighted category or regular category
        if (matchingCategory.is_highlighted) {
          setHighlightedCategories([matchingCategory]);
        } else {
          setMoreCategories([matchingCategory]);
        }
      }
    }
  }, [
    currentPlan?.name,
    allCategories?.data,
    hasAutoSelected,
    highlightedCategories.length,
    moreCategories.length,
  ]);

  const queryResult = usePipelineTemplates({
    params: {
      page,
      limit: PAGE_SIZE,
      category_labels: categories.map(({ label }) => label),
      query: debouncedSearchInput.trim() || undefined,
    },
    keepPreviousData: true,
  });

  const {
    error: templateError,
    isLoading: isSelectingTemplate,
    mutate: createPipelineFromTemplate,
  } = useCreatePipelineFromTemplate({
    onSuccess: onSelect,
  });

  const { isLoading } = queryResult;

  if (isLoading) {
    return <>Loading...</>;
  }

  function handleSelectTemplateClick(template: PipelineTemplateSummary) {
    createPipelineFromTemplate({ template });
  }

  // Hide category filters when simplified_ux is enabled
  const shouldShowFilters = !isSimplifiedUxPlan;

  return (
    <div className={c('rapid-deploy')}>
      {isSelectingTemplate && (
        <div className={c('templates-loader')}>
          <Loader text="Copying template to application..." />
        </div>
      )}

      {shouldShowFilters && (
        <div className={c('filters')}>
          <HighlightedCategoryFilter
            id="rapid-deploy_categories-filter"
            value={highlightedCategories}
            onChange={(categories) => {
              setHighlightedCategories(categories);
              setHasAutoSelected(true); // Mark as user has made manual selection
              setPage(1);
            }}
          />
          <CategorySelect
            value={moreCategories}
            onChange={(values) => {
              setMoreCategories([...values]);
              setHasAutoSelected(true); // Mark as user has made manual selection
              setPage(1);
            }}
            queryFilters={{
              only_non_highlighted: true,
            }}
            isMulti
          />
        </div>
      )}

      <QuerySearch
        value={searchInput}
        entityName="template"
        searchLabel="Search templates..."
        onValueChange={(value) => {
          setSearchInput(value ?? '');
          setPage(1);
        }}
        totalElements={queryResult.data?.total_elements}
      />

      {templateError && <Text intent="danger">{templateError.message}</Text>}

      {isLoading ? (
        <div className={c('table', 'full-width')}>
          <SkeletonList min={10} max={15} component={<LoaderRow />} />
        </div>
      ) : (
        <PaginatedTable<PipelineTemplateSummary>
          className={c('table', 'full-width', 'templates')}
          id="rapid-deploy-templates"
          label="Templates"
          queryResult={queryResult}
          columns={columns}
          pageSize={PAGE_SIZE}
          page={page}
          onRowClick={handleSelectTemplateClick}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}

function LoaderRow() {
  return (
    <div className={c('loader-table-row')}>
      <div className={c('loader-table-cell', 'auto')}>
        <div className={c('loader-image', 'skeleton-image')} />
      </div>

      <div className={c('loader-table-cell')}>
        <div className={c('template-content')}>
          <div className={c('template-categories')}>
            <Text
              className={c('template-category', 'skeleton-text')}
              size="small"
              asChild
            >
              <span aria-hidden="true">Basics</span>
            </Text>
          </div>
          <Heading level="4" asChild>
            <div className="skeleton-text" />
          </Heading>
          <Text type="paragraph">
            <div className="skeleton-text" />
            <div className="skeleton-text" />
          </Text>
        </div>
      </div>
    </div>
  );
}
